import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { getClassSessions } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassSession } from "./types";
import {
  SESSION_STATUS_COLORS,
  SessionRange,
  currentMonthRange,
  formatRangeLabel,
  formatStatusLabel,
  formatTime,
  isSessionCancelled,
  isSessionPast,
  sessionRowKey,
  shiftRangeByMonths,
} from "./classUtils";

interface ClassSeriesSessionsDialogProps {
  open: boolean;
  classId: string | null;
  className?: string;
  capacity?: number;
  /** Highlights the session the roster panel this was opened from is showing. */
  currentSessionId?: string | null;
  onClose: () => void;
}

/**
 * Read-only peek at a month of the class's sessions — dates and how many are
 * booked — reachable from inside a single session's roster panel without
 * leaving it. A recurring class has no fixed end, so this browses a window
 * rather than listing the whole series. Deliberately has no roster/cancel
 * actions of its own (that would mean nesting this panel inside itself);
 * managing a different session still happens from the Classes page's own
 * Sessions list.
 */
const ClassSeriesSessionsDialog: React.FC<ClassSeriesSessionsDialogProps> = ({
  open,
  classId,
  className,
  capacity,
  currentSessionId,
  onClose,
}) => {
  const [sessions, setSessions] = React.useState<ClassSession[]>([]);
  const [range, setRange] = React.useState<SessionRange>(() => currentMonthRange());
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (open) setRange(currentMonthRange());
  }, [open, classId]);

  React.useEffect(() => {
    if (!open || !classId) return;
    let cancelled = false;

    const fetchSessions = async () => {
      setLoading(true);
      try {
        const result = await getClassSessions(classId, range);
        if (!cancelled) setSessions(result.sessions);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load the series."));
          setSessions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSessions();
    return () => {
      cancelled = true;
    };
  }, [open, classId, range]);

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 640 }, maxWidth: 820 }}
      title={`Sessions — ${className || "Class"}`}
      actions={<CustomButton label="Close" variant="outlined" onClick={onClose} />}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <IconButton
          size="small"
          disabled={loading}
          onClick={() => setRange((prev) => shiftRangeByMonths(prev, -1))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
          {formatRangeLabel(range)}
        </Typography>
        <IconButton
          size="small"
          disabled={loading}
          onClick={() => setRange((prev) => shiftRangeByMonths(prev, 1))}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : sessions.length === 0 ? (
        <Typography sx={{ color: "#777", fontSize: 14 }}>
          No sessions in this month.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Booked</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session, index) => {
                const cancelled = isSessionCancelled(session.status);
                const projected = session.is_virtual === true;
                const statusKey = String(session.status || "").toUpperCase();
                const isCurrent =
                  Boolean(currentSessionId) &&
                  String(session.id) === String(currentSessionId);
                return (
                  <TableRow
                    key={sessionRowKey(session, index)}
                    sx={{
                      opacity: cancelled || isSessionPast(session.end_at) ? 0.6 : 1,
                      bgcolor: isCurrent ? "#e8f0fd" : undefined,
                    }}
                  >
                    <TableCell>
                      {formatTableDate(session.start_at)}
                      {projected && (
                        <Tooltip title="Generated from the class schedule — it becomes a real session the first time someone books it.">
                          <Chip
                            size="small"
                            label="Projected"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: 11,
                              borderStyle: "dashed",
                              color: "#6b46c1",
                              borderColor: "#b794f4",
                            }}
                          />
                        </Tooltip>
                      )}
                      {isCurrent && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 11, color: "#2e6fd9", ml: 1 }}
                        >
                          (this session)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {`${formatTime(session.start_at)} - ${formatTime(
                        session.end_at
                      )}`}
                    </TableCell>
                    <TableCell>
                      {typeof session.booked_count === "number"
                        ? `${session.booked_count}${capacity ? ` / ${capacity}` : ""}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatStatusLabel(session.status)}
                        sx={{
                          bgcolor: `${SESSION_STATUS_COLORS[statusKey] || "#666"}1a`,
                          color: SESSION_STATUS_COLORS[statusKey] || "#666",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </CustomDialog>
  );
};

export default ClassSeriesSessionsDialog;
